import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { RainbowMark } from "./Reveal";
import { scrollToId } from "@/lib/api";

const LINKS = [
  { label: "About Us", id: "about" },
  { label: "Services", id: "services" },
  { label: "Onboarding", id: "insurance" },
  { label: "Request Services", id: "intake" },
  { label: "Contact Us", id: "contact" },
];

const PROGRAM_LINKS = [
  { label: "Ages & Programs We Serve", to: "/ages-programs" },
  { label: "Social Skills Group", to: "/social-skills-group" },
  { label: "Adult Evening Program (18+)", to: "/adult-social-skills" },
  { label: "Telehealth ABA", to: "/ages-programs#telehealth" },
  { label: "School & Daycare Support", to: "/school-support" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToId(id);
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-brand-lightblue/40 shadow-soft"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
          onClick={(e) => {
            e.preventDefault();
            window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <RainbowMark className="h-7 w-11 transition-transform duration-500 group-hover:-translate-y-0.5" />
          <span className="leading-none">
            <span className="block font-serif text-xl font-semibold text-brand-blue tracking-tight">Milestone</span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-brand-coral font-semibold mt-0.5">
              Pediatric Therapy
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-brand-muted hover:text-brand-blue transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <div className="relative group" data-testid="nav-programs-dropdown">
            <button
              data-testid="nav-programs-toggle"
              className="flex items-center gap-1 text-sm font-medium text-brand-muted hover:text-brand-blue transition-colors duration-300"
            >
              Programs
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-[opacity,visibility] duration-300">
              <div className="w-64 rounded-2xl bg-white border border-brand-lightblue/40 shadow-lift p-2">
                {PROGRAM_LINKS.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    data-testid={`nav-program-${p.to.slice(1)}`}
                    className="block rounded-xl px-4 py-3 text-sm font-medium text-brand-ink hover:bg-brand-pale hover:text-brand-blue transition-colors duration-300"
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <button
            data-testid="nav-cta-intake"
            onClick={() => go("intake")}
            className="rounded-full bg-brand-coral text-white text-sm font-semibold px-6 py-2.5 shadow-soft transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:shadow-lift hover:bg-[#f06666]"
          >
            Start Intake
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-brand-blue p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-brand-lightblue/40 px-6 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left text-base font-medium text-brand-ink"
            >
              {l.label}
            </button>
          ))}
          <div className="pt-3 mt-1 border-t border-brand-lightblue/30">
            <span className="block text-xs uppercase tracking-[0.2em] text-brand-coral font-semibold mb-2">
              Programs
            </span>
            {PROGRAM_LINKS.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                data-testid={`nav-mobile-program-${p.to.slice(1)}`}
                onClick={() => setOpen(false)}
                className="block py-1.5 text-base font-medium text-brand-ink"
              >
                {p.label}
              </Link>
            ))}
          </div>
          <button
            data-testid="nav-mobile-cta-intake"
            onClick={() => go("intake")}
            className="rounded-full bg-brand-coral text-white text-sm font-semibold px-6 py-3 mt-2 w-fit"
          >
            Start Intake
          </button>
        </div>
      )}
    </header>
  );
};
